import { useState, useCallback, useEffect } from 'react';
import { History, Plus } from 'lucide-react';
import { MessageList } from './MessageList';
import { ChatInput } from './ChatInput';
import { ProtocolSelector } from './ProtocolSelector';
import { useIntelligence } from '../../contexts';
import api from '../../../services/api';
import type { ChatMessage, ChatResponse, ProtocolContext, SourceCitation } from '../../types';

interface ChatContainerProps {
  initialMessages?: ChatMessage[];
  initialProtocolContext?: ProtocolContext | null;
  isLoadingConversation?: boolean;
  onSourceClick?: (source: SourceCitation) => void;
  onNewChat?: () => void;
  onToggleSidebar?: () => void;
  isMobile?: boolean;
}

export function ChatContainer({
  initialMessages = [],
  initialProtocolContext,
  isLoadingConversation = false,
  onSourceClick,
  onNewChat,
  onToggleSidebar,
  isMobile = false,
}: ChatContainerProps) {
  const { currentProtocol, setCurrentProtocol, protocols, currentConversation, loadConversations } = useIntelligence();
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [isLoading, setIsLoading] = useState(false);
  const [tokenLimitReached, setTokenLimitReached] = useState(false);
  // Track conversation ID locally to avoid remounting when conversation is created
  const [localConversationId, setLocalConversationId] = useState<string | null>(currentConversation?.id || null);

  // Update messages when initial messages change (conversation selected)
  useEffect(() => {
    setMessages(initialMessages);
    setTokenLimitReached(false);
  }, [initialMessages]);

  // Sync local conversation ID with context when switching conversations
  useEffect(() => {
    setLocalConversationId(currentConversation?.id || null);
  }, [currentConversation?.id]);

  // Reset local conversation ID when starting a fresh chat (initialMessages cleared)
  // This handles the case where user clicks "New Chat" while already in an unsaved new chat
  useEffect(() => {
    if (initialMessages.length === 0 && !currentConversation) {
      setLocalConversationId(null);
    }
  }, [initialMessages.length, currentConversation]);

  const handleSendMessage = useCallback(async (content: string) => {
    // Add user message immediately
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);

    // Add loading placeholder for assistant
    const loadingMessage: ChatMessage = {
      id: `loading-${Date.now()}`,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isLoading: true,
    };
    setMessages(prev => [...prev, loadingMessage]);
    setIsLoading(true);

    try {
      // Use local conversation ID or let backend create one
      let conversationId = localConversationId;

      // Send message to backend - it will create conversation if needed
      const response = await api.post<ChatResponse>('/api/intelligence/chat', {
        message: content,
        conversation_id: conversationId || undefined,
        protocol_id: currentProtocol?.id,
      });

      // If this was a new conversation, store the ID locally and refresh sidebar
      if (!conversationId && response.data.conversation_id) {
        const newConvId = response.data.conversation_id;
        setLocalConversationId(newConvId);

        // Refresh sidebar so the new conversation appears in the list
        // NOTE: We intentionally do NOT call setCurrentConversation here because
        // that would change the key prop and cause this component to remount,
        // losing all local message state. The localConversationId keeps the
        // session working, and the conversation is persisted in the database.
        await loadConversations();
      }

      // Check if token limit was reached
      if (response.data.token_limit_reached) {
        setTokenLimitReached(true);
      }

      // Replace loading message with actual response
      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response.data.message,
        sources: response.data.sources,
        timestamp: new Date(),
        protocolContext: response.data.protocol_context,
      };

      setMessages(prev =>
        prev.map(msg =>
          msg.isLoading ? assistantMessage : msg
        )
      );
    } catch (error: any) {
      console.error('Error sending message:', error);

      // Handle rate limit (429) — show the message from the backend
      const rateLimited = error?.response?.status === 429;
      const errorContent = rateLimited && error?.response?.data?.message
        ? error.response.data.message
        : 'I apologize, but I encountered an error processing your request. Please try again.';

      if (rateLimited) {
        setTokenLimitReached(true);
      }

      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: errorContent,
        timestamp: new Date(),
      };

      setMessages(prev =>
        prev.map(msg =>
          msg.isLoading ? errorMessage : msg
        )
      );
    } finally {
      setIsLoading(false);
    }
  }, [currentProtocol, localConversationId, loadConversations]);

  return (
    <div className={`flex flex-col h-full bg-gray-50 dark:bg-gray-900 ${isMobile ? '' : 'rounded-xl border border-gray-200 dark:border-gray-700'} overflow-hidden`}>
      {/* Mobile header bar */}
      {isMobile && (
        <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 bg-white border-b border-gray-200 dark:bg-gray-800 dark:border-gray-700">
          <button
            onClick={onToggleSidebar}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700"
            aria-label="Chat history"
          >
            <History size={20} className="text-gray-600 dark:text-gray-300" />
          </button>
          <span className="font-medium text-gray-900 dark:text-gray-100">Chat</span>
          <button
            onClick={onNewChat}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700"
            aria-label="New chat"
          >
            <Plus size={20} className="text-gray-600 dark:text-gray-300" />
          </button>
        </div>
      )}

      {/* Protocol selector - locked once chat has messages (must start new chat to change) */}
      <div className="flex-shrink-0">
        <ProtocolSelector
          protocols={protocols}
          selectedProtocol={currentProtocol}
          onSelect={setCurrentProtocol}
          disabled={messages.length > 0}
        />
      </div>

      {/* Loading state when switching conversations */}
      {isLoadingConversation ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
            <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            <span>Loading conversation...</span>
          </div>
        </div>
      ) : (
        <>
          {/* Messages area - scrollable, takes remaining space */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <MessageList messages={messages} onSourceClick={onSourceClick} />
          </div>

          {/* Input - fixed at bottom */}
          <div className="flex-shrink-0">
            {tokenLimitReached ? (
              <div className="p-4 bg-amber-50 border-t border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
                <p className="text-sm text-amber-800 text-center mb-2 dark:text-amber-400">
                  This conversation has reached its token limit.
                </p>
                <button
                  onClick={onNewChat}
                  className="w-full py-2 px-4 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  Start New Chat
                </button>
              </div>
            ) : (
              <ChatInput
                onSend={handleSendMessage}
                isLoading={isLoading}
                placeholder={
                  currentProtocol
                    ? `Ask about ${currentProtocol.protocol_number}...`
                    : 'Ask about a protocol'
                }
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
